import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import { useTranslation } from 'react-i18next';

const Documentation = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const steps = [
    {
      icon: PersonAddAltOutlinedIcon,
      color: theme.palette.primary.main,
      title: t('Registration and Login'),
      description: t('Create an account or log in using your credentials'),
    },
    {
      icon: StorefrontOutlinedIcon,
      color: theme.palette.secondary.main,
      title: t('Profile Setup'),
      description: t('Go to the Profile section and fill in the necessary information about your business'),
    },
    {
      icon: RestaurantMenuOutlinedIcon,
      color: theme.palette.warning.main,
      title: t('Menu Creation'),
      bullets: [
        t('Create groups to organize your menu items by categories (e.g., appetizers, main courses, drinks)'),
        t('In the Items section, add specific menu items, specifying the name, description, price, and group'),
        t('Use the AI image generation feature to create professional photos for your dishes'),
        t('Enable automatic translation to make your menu multilingual'),
        t('Import menu from Excel template or export existing menu to Excel'),
      ],
    },
    {
      icon: ReceiptLongOutlinedIcon,
      color: theme.palette.success.main,
      title: t('Order Management'),
      description: t('In the Orders section, you can view and process incoming orders in real-time with sound notifications'),
    },
    {
      icon: QrCode2OutlinedIcon,
      color: theme.palette.info.main,
      title: t('QR Code Generation'),
      description: t('In the QR Codes section, create and manage QR codes for tables or takeout locations'),
    },
    {
      icon: InsightsOutlinedIcon,
      color: theme.palette.secondary.main,
      title: t('Statistics Analysis'),
      description: t('Use the Statistics section to view data on sales, popular items, and revenue by different time periods'),
    },
    {
      icon: SettingsOutlinedIcon,
      color: theme.palette.primary.main,
      title: t('System Setup'),
      description: t('In the Settings section, you can configure delivery options, payment methods, notifications, and other platform parameters'),
    },
    {
      icon: AutorenewOutlinedIcon,
      color: theme.palette.warning.main,
      title: t('Regular Updates'),
      description: t('Keep your menu, prices, and business information up-to-date for the best customer experience'),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 1,
            color: 'text.primary',
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          {t('How to Use the Platform')} 📖
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t('A step-by-step guide to get the most out of your dashboard')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(step.color, 0.15)}`,
                  },
                }}
              >
                <CardContent sx={{ display: 'flex', gap: 2 }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 48,
                      height: 48,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(step.color, 0.12),
                      color: step.color,
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: step.color }}
                      >
                        {t('Step')} {index + 1}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {step.title}
                    </Typography>
                    {step.description && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {step.description}
                      </Typography>
                    )}
                    {step.bullets && (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {step.bullets.map((bullet, i) => (
                          <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box
                              sx={{
                                mt: '7px',
                                flexShrink: 0,
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: step.color,
                              }}
                            />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {bullet}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Footer note */}
      <Card
        sx={{
          mt: 3,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.06
          )} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            {t('For more detailed help on each section, refer to the detailed instructions available in the corresponding sections of the platform')}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Documentation;
